import { useEndImpersonationMutation } from '@features/users/usersApi';
import { SettingsContext } from '@contexts/SettingsContext';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SnackbarKey, useSnackbar } from 'notistack';
import { useCallback, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export const useImpersonation = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { impersonated, user } = useContext(SettingsContext);
  const [endImpersonation] = useEndImpersonationMutation();

  const handleClick = useCallback(
    async (snackbarId: SnackbarKey) => {
      try {
        await endImpersonation().unwrap();
      } catch (error) {
        console.error(error);
        enqueueSnackbar(
          'Unable to end impersonation. Try reloading the page.',
          {
            variant: 'error',
            autoHideDuration: null,
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
            preventDuplicate: true,
          }
        );
        return;
      }

      void navigate('/users');
      location.reload();
      closeSnackbar(snackbarId);
    },
    [closeSnackbar, endImpersonation, enqueueSnackbar, navigate]
  );

  useEffect(() => {
    if (!impersonated) return;

    const action = (snackbarId: SnackbarKey) => {
      return (
        <FontAwesomeIcon
          icon={faXmark}
          cursor="pointer"
          onClick={() => void handleClick(snackbarId)}
        />
      );
    };
    enqueueSnackbar(
      `You're impersonating ${user.first_name} ${user.last_name} (${user.email})`,
      {
        variant: 'info',
        autoHideDuration: null,
        anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        preventDuplicate: true,
        action,
      }
    );
  }, [
    impersonated,
    user,
    enqueueSnackbar,
    navigate,
    closeSnackbar,
    endImpersonation,
    handleClick,
  ]);
};
